/**
 * Product/Inventory data models.
 */

import {
  BaseEntity,
  TimestampMixin,
  SearchableEntity,
  ImageEntity,
  DEFAULT_STRINGS,
  DEFAULT_NUMBERS,
} from './base';
import type { ProfitPlus2k8ProductIntegration } from './integrations/profitPlus2k8';
import type { ProductStats } from './productStats';

export type ProductType = 'physical' | 'service' | 'digital' | 'bundle';

export const PRODUCT_TYPE_VALUES: ProductType[] = [
  'physical',
  'service',
  'digital',
  'bundle',
];

export const PRODUCT_TYPE_LABELS: Record<ProductType, string> = {
  physical: 'Físico (inventariable)',
  service: 'Servicio',
  digital: 'Digital',
  bundle: 'Kit / combo',
};

/** Spanish list for UI hints, e.g. "Físico, servicio, digital o kit/combo". */
export function formatProductTypeList(): string {
  const labels = PRODUCT_TYPE_VALUES.map(type => PRODUCT_TYPE_LABELS[type]);
  if (labels.length <= 1) return labels[0] ?? '';
  return `${labels.slice(0, -1).join(', ')} o ${labels[labels.length - 1]}`;
}

export type WeightUnit = 'kg' | 'lb' | 'g';
export type DimensionUnit = 'cm' | 'in' | 'm';

export interface ProductImage {
  url: string;
  storagePath: string;
}

export interface ProductBase extends SearchableEntity, ImageEntity {
  code: string;
  reference?: string;
  name: string;
  taxId?: string;
  specifications?: string;
  department?: string;
  subDepartment?: string;
  brand?: string;
  availability?: number;
  price?: number;
  measurement?: string;
  batch?: string;
  size?: string;
  color?: string;
  packagingQuantity?: string;
  minSalesQuantity?: number;
  description?: string;
  cost?: number;
  special?: boolean;
  images?: ProductImage[]; // Array of additional images (excluding principal image)

  /** Whether the product is available for sale (false = discontinued/archived). */
  active?: boolean;
  /** Drives inventory behavior and catalog grouping. */
  productType?: ProductType;
  /** When false, availability is informational only (services, digital goods). */
  trackInventory?: boolean;

  /** EAN/UPC or other scannable identifier. */
  barcode?: string;
  /** Supplier SKU, manufacturer ref, legacy codes, etc. */
  alternateCodes?: string[];

  /** Sale unit (e.g. unit, kg, box). */
  unitOfMeasure?: string;
  /** Purchase/receiving unit when different from sale unit. */
  purchaseUnitOfMeasure?: string;
  /** Units contained in one sellable package. */
  unitsPerPackage?: number;
  /** Minimum quantity increment per sale line. */
  saleIncrement?: number;
  maxSalesQuantity?: number;

  stockMin?: number;
  stockMax?: number;
  reorderPoint?: number;

  weight?: number;
  weightUnit?: WeightUnit;
  length?: number;
  width?: number;
  height?: number;
  dimensionUnit?: DimensionUnit;

  /** Profit Plus 2k8 (ERP): one field per integration – native fields (dbo.art) + syncedAt. */
  profitPlus2k8?: ProfitPlus2k8ProductIntegration;

  /** Rolling price/cost/margin/sales/availability aggregates. */
  stats?: ProductStats;
}

export interface Product extends ProductBase, BaseEntity, TimestampMixin {
  // Inherits all fields from ProductBase + BaseEntity + TimestampMixin
}

export interface ProductCreate extends Omit<ProductBase, 'keywords'> {
  // For creating new products - keywords are generated automatically
  /** Create-only: warehouse for initial stock when multi-warehouse is enabled */
  initialWarehouseId?: string;
}

export interface ProductUpdate extends Partial<ProductBase> {
  updated?: Date;
}

// Default values for products
export const PRODUCT_DEFAULTS = {
  reference: DEFAULT_STRINGS.UNDEFINED,
  specifications: DEFAULT_STRINGS.UNDEFINED,
  department: DEFAULT_STRINGS.UNDEFINED,
  subDepartment: DEFAULT_STRINGS.UNDEFINED,
  brand: DEFAULT_STRINGS.UNDEFINED,
  availability: DEFAULT_NUMBERS.ZERO,
  price: DEFAULT_NUMBERS.ZERO,
  measurement: DEFAULT_STRINGS.UNDEFINED,
  batch: 'NO',
  size: DEFAULT_STRINGS.UNDEFINED,
  color: DEFAULT_STRINGS.UNDEFINED,
  packagingQuantity: '',
  minSalesQuantity: DEFAULT_NUMBERS.MIN_QUANTITY,
  description: DEFAULT_STRINGS.UNDEFINED,
  cost: DEFAULT_NUMBERS.ZERO,
  special: false,
  active: true,
  productType: 'physical' as ProductType,
  trackInventory: true,
  barcode: DEFAULT_STRINGS.UNDEFINED,
  alternateCodes: [] as string[],
  unitOfMeasure: DEFAULT_STRINGS.UNDEFINED,
  purchaseUnitOfMeasure: DEFAULT_STRINGS.UNDEFINED,
  saleIncrement: DEFAULT_NUMBERS.MIN_QUANTITY,
  weightUnit: 'kg' as WeightUnit,
  dimensionUnit: 'cm' as DimensionUnit,
} as const;

// Product search and filter types
export interface ProductFilters {
  searchTerm?: string;
  department?: string;
  brand?: string;
  minPrice?: number;
  maxPrice?: number;
  inStock?: boolean;
  special?: boolean;
  active?: boolean;
  productType?: ProductType;
}

export interface ProductQueryResult {
  products: Product[];
  lastDoc?: any; // Firestore document snapshot
  hasMore: boolean;
  totalCount?: number;
}
